import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { APP_NAME, SUPPORT_EMAIL } from "../constants/expiry";
import { SentrilogIcon } from "../components/Icons";
import { useAuth } from "../hooks/useAuth";

const CheckIcon: React.FC = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const PLANS = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    desc: "Perfect for individuals tracking personal or business documents.",
    highlight: false,
    cta: "Get Started Free",
    href: "/register",
    features: [
      "Up to 15 documents",
      "Email reminders (30, 7, 1 day)",
      "5 AI document scans / month",
      "10 AI chat messages / day",
      "Risk dashboard",
      "1 user",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    price: "$15",
    period: "/month",
    desc: "Unlimited documents and CSV import for solo operators.",
    highlight: false,
    badge: null as string | null,
    cta: "Subscribe to Pro",
    href: "/register",
    features: [
      "Unlimited documents",
      "Up to 5 team members",
      "100 AI scans / month",
      "Unlimited AI chat",
      "CSV import & export",
      "Custom reminder intervals",
      "Audit trail",
      "Snooze reminders",
    ],
  },
  {
    key: "business",
    name: "Business",
    price: "$35",
    period: "/month",
    desc: "Full compliance suite for growing teams.",
    highlight: true,
    badge: "Most Popular",
    cta: "Subscribe to Business",
    href: "/register",
    features: [
      "Everything in Pro",
      "Up to 25 team members",
      "Unlimited AI scans",
      "Custom email templates",
      "Slack / webhook alerts",
      "Multi-workspace",
      "Priority support",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "Custom",
    period: "",
    desc: "For large organisations with complex compliance needs.",
    highlight: false,
    badge: null as string | null,
    cta: "Contact Sales",
    href: `mailto:${SUPPORT_EMAIL}?subject=Enterprise%20Enquiry`,
    features: [
      "Everything in Business",
      "Unlimited team members",
      "API access",
      "White-label option",
      "SSO / SAML",
      "Dedicated account manager",
      "SLA & uptime guarantee",
      "Custom integrations",
      "On-premise available",
    ],
  },
];

const FAQ_ITEMS = [
  {
    q: "Do I need a credit card to sign up?",
    a: "No. The Free plan requires no payment details. You only enter card information when you choose to upgrade to a paid plan.",
  },
  {
    q: "Can I upgrade or downgrade at any time?",
    a: "Yes. You can switch plans at any time. Upgrades take effect immediately; downgrades take effect at the end of the current billing period.",
  },
  {
    q: "What payment methods are accepted?",
    a: "We accept all major credit and debit cards (Visa, Mastercard, Amex, Discover) via our secure payment provider.",
  },
  {
    q: "Is there a refund policy?",
    a: (
      <>
        Yes. We offer a 7-day refund on new subscriptions. See our{" "}
        <a href="/refund" className="text-[#2563EB] underline underline-offset-2">
          Refund Policy
        </a>{" "}
        for full details.
      </>
    ),
  },
  {
    q: "What happens to my data if I downgrade?",
    a: "Your data is never deleted automatically. If you exceed Free-plan limits after downgrading, you will have read-only access until you remove items or upgrade again.",
  },
  {
    q: "Do you offer discounts for non-profits or startups?",
    a: `We consider case-by-case discounts. Contact us at ${SUPPORT_EMAIL} with details about your organisation.`,
  },
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
              <SentrilogIcon className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-[#111827] text-lg">{APP_NAME}</span>
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link
                to="/items"
                className="px-4 py-2 bg-[#2563EB] text-white text-sm font-semibold rounded-lg hover:bg-[#1D4ED8] transition-colors"
              >
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-[#2563EB] text-white text-sm font-semibold rounded-lg hover:bg-[#1D4ED8] transition-colors"
                >
                  Get started free
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-16 text-center bg-white border-b border-[#F3F4F6]">
          <div className="max-w-3xl mx-auto px-4">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-3">
              Simple pricing
            </p>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#111827] mb-4">
              Start Free. Scale as You Grow.
            </h1>
            <p className="text-[#6B7280] text-lg max-w-xl mx-auto mb-6">
              No hidden fees. No credit card needed to get started. Cancel
              anytime.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EFF6FF] border border-[#BFDBFE] rounded-full text-xs font-semibold text-[#1D4ED8]">
              🔒 Secure payments — all major cards accepted
            </div>
          </div>
        </section>

        {/* Plans grid */}
        <section className="py-16 bg-[#F9FAFB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {PLANS.map(
                ({ key, name, price, period, desc, highlight, badge, cta, href, features }) => (
                  <div
                    key={key}
                    className={`rounded-2xl p-7 flex flex-col ${
                      highlight
                        ? "bg-[#2563EB] text-white shadow-2xl shadow-blue-500/30 border-2 border-[#1D4ED8] scale-[1.02]"
                        : "bg-white border border-[#E5E7EB] shadow-sm"
                    }`}
                  >
                    {badge && (
                      <span
                        className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-full mb-3 w-fit ${
                          highlight
                            ? "bg-white/20 text-white"
                            : "bg-[#DBEAFE] text-[#1D4ED8]"
                        }`}
                      >
                        {badge}
                      </span>
                    )}
                    <h2
                      className={`font-bold text-lg mb-1 ${highlight ? "text-white" : "text-[#111827]"}`}
                    >
                      {name}
                    </h2>
                    <div className="flex items-baseline gap-0.5 mb-2">
                      <span
                        className={`text-3xl font-extrabold ${highlight ? "text-white" : "text-[#111827]"}`}
                      >
                        {price}
                      </span>
                      {period && (
                        <span
                          className={`text-sm ml-1 ${highlight ? "text-blue-200" : "text-[#9CA3AF]"}`}
                        >
                          {period}
                        </span>
                      )}
                    </div>
                    <p
                      className={`text-xs mb-5 ${highlight ? "text-blue-100" : "text-[#6B7280]"}`}
                    >
                      {desc}
                    </p>
                    <ul className="space-y-2.5 mb-7 flex-1">
                      {features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs">
                          <span
                            className={`mt-0.5 flex-shrink-0 ${highlight ? "text-blue-200" : "text-[#22C55E]"}`}
                          >
                            <CheckIcon />
                          </span>
                          <span
                            className={highlight ? "text-blue-50" : "text-[#374151]"}
                          >
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {key === "enterprise" ? (
                      <a
                        href={href}
                        className="w-full py-2.5 rounded-xl font-semibold text-sm text-center transition-colors bg-[#111827] text-white hover:bg-[#1F2937]"
                      >
                        {cta}
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate(isAuthenticated ? "/items" : href)}
                        className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                          highlight
                            ? "bg-white text-[#2563EB] hover:bg-blue-50"
                            : "bg-[#EFF6FF] text-[#2563EB] hover:bg-[#DBEAFE] border border-[#BFDBFE]"
                        }`}
                      >
                        {isAuthenticated && key !== "free" ? "Go to Dashboard" : cta}
                      </button>
                    )}
                  </div>
                )
              )}
            </div>

            <p className="text-center text-xs text-[#9CA3AF] mt-8">
              All plans include automated expiry reminders and a risk dashboard.
              Prices are in USD. Taxes may apply.{" "}
              <a href="/refund" className="text-[#2563EB] hover:underline">
                Refund policy
              </a>
              .
            </p>
          </div>
        </section>

        {/* Feature comparison table */}
        <section className="py-16 bg-white border-t border-[#F3F4F6]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-extrabold text-[#111827] text-center mb-8">
              Plan comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left font-semibold text-[#374151] pb-3 pr-4 w-1/3">
                      Feature
                    </th>
                    {["Free", "Pro", "Business", "Enterprise"].map((p) => (
                      <th
                        key={p}
                        className={`text-center font-semibold pb-3 px-2 ${p === "Business" ? "text-[#2563EB]" : "text-[#374151]"}`}
                      >
                        {p}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                  {[
                    ["Documents", "15", "Unlimited", "Unlimited", "Unlimited"],
                    ["Team members", "1", "5", "25", "Unlimited"],
                    ["AI scans / month", "5", "100", "Unlimited", "Unlimited"],
                    ["AI chat / day", "10", "Unlimited", "Unlimited", "Unlimited"],
                    ["Email reminders", "✓", "✓", "✓", "✓"],
                    ["CSV import", "—", "✓", "✓", "✓"],
                    ["Custom reminder schedule", "—", "✓", "✓", "✓"],
                    ["Audit trail", "—", "✓", "✓", "✓"],
                    ["Snooze reminders", "—", "✓", "✓", "✓"],
                    ["Custom email templates", "—", "—", "✓", "✓"],
                    ["Slack / webhook alerts", "—", "—", "✓", "✓"],
                    ["Multi-workspace", "—", "—", "✓", "✓"],
                    ["API access", "—", "—", "—", "✓"],
                    ["White-label", "—", "—", "—", "✓"],
                    ["SSO / SAML", "—", "—", "—", "✓"],
                    ["Dedicated support", "—", "—", "Priority", "Dedicated"],
                  ].map(([feature, ...vals]) => (
                    <tr key={feature}>
                      <td className="py-2.5 pr-4 text-[#374151] font-medium">
                        {feature}
                      </td>
                      {vals.map((v, i) => (
                        <td
                          key={i}
                          className={`py-2.5 px-2 text-center ${
                            v === "—"
                              ? "text-[#D1D5DB]"
                              : v === "✓" || v === "Unlimited"
                                ? "text-[#059669] font-semibold"
                                : "text-[#374151]"
                          }`}
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-[#F9FAFB] border-t border-[#F3F4F6]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-extrabold text-[#111827] text-center mb-8">
              Frequently asked questions
            </h2>
            <div className="space-y-4">
              {FAQ_ITEMS.map(({ q, a }) => (
                <div
                  key={q}
                  className="bg-white rounded-xl border border-[#E5E7EB] px-6 py-5 shadow-sm"
                >
                  <p className="font-semibold text-[#111827] mb-2">{q}</p>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#2563EB] text-white text-center">
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-3xl font-extrabold mb-4">
              Ready to stay ahead of expiry?
            </h2>
            <p className="text-blue-100 mb-8">
              Start free. No credit card. Setup in under 1 minute.
            </p>
            <Link
              to="/register"
              className="inline-block px-8 py-3.5 bg-white text-[#2563EB] font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Start for free →
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#111827] text-[#6B7280] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            <a href="/refund" className="hover:text-white transition-colors">Refund Policy</a>
            <a href="/cookie-policy" className="hover:text-white transition-colors">Cookies</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
